import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@wc/domain/authn";

import { createTeam } from "../core/create-team";

/**
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {(isOpen: boolean) => void} props.onOpenChange Callback when modal open state changes
 * @param {string} props.organizationId The organization to create the team in
 * @param {() => void} [props.onSuccess] Callback after successful creation
 * @returns {React.ReactElement} The create team modal
 */
export default function CreateTeamModal({
  isOpen,
  onOpenChange,
  organizationId,
  onSuccess,
}) {
  const { t } = useTranslation();
  const authData = useAuth();
  const [teamName, setTeamName] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleCreate = async () => {
    if (!teamName.trim()) {
      setError("Team name is required");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await createTeam(
        { name: teamName.trim(), organizationId },
        authData.getCurrentUser(),
      );

      setTeamName("");
      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Failed to create team:", err);
      if (err.message?.includes("already exists")) {
        setError(t("team.duplicateName"));
      } else {
        setError("Failed to create team. Please try again.");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setTeamName("");
    setError(null);
    onOpenChange(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleClose}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {t("team.createTeam")}
        </ModalHeader>
        <ModalBody>
          <Input
            autoFocus
            label={t("team.teamNameLabel")}
            placeholder={t("team.teamNamePlaceholder")}
            value={teamName}
            onValueChange={setTeamName}
            isDisabled={isCreating}
            isInvalid={!!error}
            errorMessage={error}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isCreating) {
                handleCreate();
              }
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose} isDisabled={isCreating}>
            {t("cancel")}
          </Button>
          <Button
            color="primary"
            onPress={handleCreate}
            isLoading={isCreating}
            isDisabled={isCreating || !teamName.trim()}
          >
            {isCreating ? t("team.creating") : t("team.createTeam")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
